// ============================================
// SelectDropdown Component
// Styled dropdown select with options
// ============================================

import { useState, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { ChevronDownIcon } from '../icons';

// ============================================
// Types
// ============================================

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectDropdownProps {
  /** Options list */
  options: SelectOption[];
  /** Selected value */
  value: string | null;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text when no value selected */
  placeholder?: string;
  /** Dropdown label */
  label?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Error message */
  error?: string;
  /** Width (CSS value) */
  width?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Additional className */
  className?: string;
}

// ============================================
// Component
// ============================================

export function SelectDropdown({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label,
  disabled = false,
  error,
  width,
  size = 'md',
  className,
}: SelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Find selected option
  const selectedOption = options.find((opt) => opt.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const sizeStyles = {
    sm: 'h-9 text-xs',
    md: 'h-10 text-sm',
  };

  return (
    <div ref={containerRef} className={cn('relative', className)} style={{ width }}>
      {/* Label */}
      {label && (
        <label className="mb-1.5 block text-xs font-medium text-slate-700">
          {label}
        </label>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border bg-white px-3 shadow-sm transition-colors',
          sizeStyles[size],
          disabled
            ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400'
            : isOpen
            ? 'border-primary-500 ring-1 ring-primary-500'
            : error
            ? 'border-red-300 hover:border-red-400'
            : 'border-slate-200 hover:border-slate-300'
        )}
      >
        {/* `title`: nút trigger buộc phải `truncate` (một dòng, cao cố định),
            nên nhãn dài vẫn bị cắt ở đây dù menu bên dưới đã cho xuống dòng.
            Tooltip là đường đọc trọn giá trị mà không phải mở menu ra. */}
        <span
          title={selectedOption?.label || placeholder}
          className={cn(
            'truncate',
            selectedOption ? 'text-slate-900' : 'text-slate-400'
          )}
        >
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDownIcon
          size={16}
          className={cn(
            'shrink-0 text-slate-400 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Error message */}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {/* Dropdown menu */}
      {isOpen && (
        /* Bug #254 — vùng highlight không phủ hết giá trị dài.
           Nguyên nhân: nhãn dài KHÔNG CÓ CHỖ XUỐNG DÒNG nên chữ chạy ngang ra
           ngoài, còn nền của mục đang chọn thì dừng ở mép menu — nhìn ra như
           highlight bị cắt ngang giữa chữ.
           Cách chữa nằm ở `whitespace-normal break-words` tại nút mục bên dưới.

           BỀ RỘNG PHẢI BÁM ĐÚNG TRIGGER (`left-0 right-0`).
           Bản sửa #254 đầu tiên còn cho menu nở rộng hơn trigger
           (`w-max max-w-[min(24rem,90vw)]`) để đỡ phải xuống dòng. Sai: menu là
           `absolute` nên nó nở sang phải mà KHÔNG biết gì về khung chứa nó —
           đặt một dropdown gần mép phải của modal là menu chọc ra ngoài, modal
           mọc thanh cuộn ngang, và nhãn vẫn bị cắt (đã gặp ở modal Test
           promotions). Neo hai mép vào trigger thì không bao giờ tràn, dù nằm
           ở đâu.
           `overflow-x-hidden` giữ lại làm chốt chặn cuối. */
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto overflow-x-hidden overscroll-contain rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => !option.disabled && handleSelect(option.value)}
              disabled={option.disabled}
              className={cn(
                // `whitespace-normal break-words`: nhãn dài xuống dòng TRONG
                // nút, nhờ đó nền highlight bao trọn chữ (bug #254).
                'flex w-full items-center whitespace-normal break-words px-3 py-2 text-left text-sm transition-colors',
                option.value === value
                  ? 'bg-primary-50 text-primary-600'
                  : option.disabled
                  ? 'cursor-not-allowed text-slate-300'
                  : 'text-slate-700 hover:bg-slate-50'
              )}
            >
              {option.label}
            </button>
          ))}
          {options.length === 0 && (
            <div className="px-3 py-2 text-center text-sm text-slate-400">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
}
